import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  PlayIcon,
  CheckCircleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import LandingHeroBackground from './LandingHeroBackground';
import DemoCallWidget from './DemoCallWidget';
import DemoVideoModal from './DemoVideoModal';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import './LandingHeroSection.css';

interface LandingHeroSectionProps {
  user: { email?: string } | null;
  loading: boolean;
  onStartTrial: () => void;
}

const LandingHeroSection: React.FC<LandingHeroSectionProps> = ({ user, loading, onStartTrial }) => {
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navLinks = [
    { id: 'features', label: 'Features' },
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'value', label: 'Why choose us?' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'faq', label: 'FAQ' },
  ];

  const handleWatchDemo = () => {
    track(AnalyticsEvents.LANDING_DEMO_CLICK);
    setShowDemoVideo(true);
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="landing-hero relative overflow-hidden text-white"
    >
      <header className="landing-hero__nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between">
            <a href="#" aria-label="Go to top">
              <Logo className="h-6 sm:h-8" />
            </a>
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className="text-white/80 hover:text-white transition-colors text-sm"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            <div className="hidden sm:flex items-center space-x-2 sm:space-x-5">
              {!loading && user ? (
                <>
                  <span className="text-white/90 text-sm sm:text-base">
                    Welcome back,{' '}
                    <span className="font-semibold text-sky-300">
                      {user.email?.split('@')[0] || 'User'}
                    </span>
                  </span>
                  <Link
                    to="/dashboard"
                    className="relative px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-cyan-400 to-sky-500 text-white rounded-lg font-medium overflow-hidden group text-sm sm:text-base shadow-lg shadow-cyan-500/20 hover:shadow-cyan-400/30 transition-all duration-300"
                  >
                    <span className="relative z-10">Go to Dashboard</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-sky-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="relative px-3 sm:px-4 py-1.5 sm:py-2 text-white/90 hover:text-white transition-colors font-medium group text-sm sm:text-base"
                  >
                    <span className="relative z-10">Log In</span>
                    <span className="absolute bottom-1 left-1/2 w-0 h-0.5 bg-sky-300 transition-all duration-300 group-hover:w-4/5 group-hover:left-[10%]"></span>
                  </Link>
                  <Link
                    to="/signup"
                    className="relative px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-cyan-400 to-sky-500 text-white rounded-lg font-medium overflow-hidden group text-sm sm:text-base shadow-lg shadow-cyan-500/20 hover:shadow-cyan-400/30 transition-all duration-300"
                  >
                    <span className="relative z-10">Get Started</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-sky-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="absolute inset-0 bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Link>
                </>
              )}
            </div>
            <button
              type="button"
              className="sm:hidden p-2 rounded-md border border-white/20 bg-white/5 hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-5 w-5 text-white" />
              ) : (
                <Bars3Icon className="h-5 w-5 text-white" />
              )}
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="sm:hidden mt-3 rounded-xl border border-white/15 bg-slate-900/80 backdrop-blur-md p-3">
              <nav className="flex flex-col gap-2 mb-3">
                {navLinks.map((link) => (
                  <a
                    key={link.id}
                    href={`#${link.id}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-white/85 hover:text-white px-2 py-1.5 rounded-md hover:bg-white/5"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 text-center px-3 py-2 text-sm rounded-md border border-white/20 text-white/90 hover:text-white hover:bg-white/5"
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 text-center px-3 py-2 text-sm rounded-md bg-gradient-to-r from-cyan-400 to-sky-500 text-white"
                >
                  Get Started
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      <LandingHeroBackground />

      <div className="landing-hero__content relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-14 md:pt-16 pb-14 sm:pb-16 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-center landing-hero__viewport-grid">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left lg:col-span-5"
          >
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-white/5 backdrop-blur-sm border border-white/10 mb-6 text-slate-300">
              <span className="h-2 w-2 rounded-full bg-emerald-400 mr-2 shadow-[0_0_8px_rgba(52,211,153,0.6)]"></span>
              The future of sales is here
            </motion.div>
            <motion.h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-4 sm:mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <span className="block text-white">AI-Powered</span>
              <span className="bg-gradient-to-r from-cyan-300 via-sky-400 to-cyan-400 bg-clip-text text-transparent">Sales Calling</span>
              <span className="block text-white">Reinvented</span>
            </motion.h1>
            <motion.p className="text-base sm:text-lg md:text-xl text-slate-400 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              Transform your sales process with our intelligent calling assistant. Make more calls, generate better leads, and close more deals—all on autopilot.
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <button onClick={onStartTrial} className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-400 to-sky-500 hover:from-cyan-300 hover:to-sky-400 text-white font-medium rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl hover:shadow-cyan-500/25 flex items-center justify-center text-sm sm:text-base shadow-lg shadow-cyan-500/20">
                {user ? 'Go to Dashboard' : 'Start with 20 Free Credits'}
                <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button onClick={handleWatchDemo} className="px-6 sm:px-8 py-3 sm:py-4 bg-white/5 hover:bg-white/15 backdrop-blur-sm text-white font-medium rounded-lg transition-all duration-300 border border-white/20 hover:border-white/30 flex items-center justify-center text-sm sm:text-base">
                <PlayIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Watch Video
              </button>
            </motion.div>
            <motion.div className="mt-6 sm:mt-8 flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 text-slate-400 text-xs sm:text-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
              <div className="flex items-center">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                20 free credits
              </div>
            </motion.div>
          </motion.div>

          {/* LT-611: the live "get called by our AI now" demo widget is the
              hero centerpiece — visible the moment a visitor lands. */}
          <div className="lg:col-span-7 flex justify-center lg:justify-end">
            <DemoCallWidget />
          </div>
        </div>
      </div>
      <DemoVideoModal isOpen={showDemoVideo} onClose={() => setShowDemoVideo(false)} />
    </motion.section>
  );
};

export default LandingHeroSection;
